import { useEffect, useState } from "react";
import { RotateCcw, Trash2, Trash } from "lucide-react";
import clsx from "clsx";
import type { BinCard } from "../lib/types";
import { accentClasses } from "../lib/types";
import { api } from "../lib/api";
import { useConfirm } from "../lib/confirm";

type Props = {
  onChanged: () => void;
};

const DAY = 24 * 60 * 60 * 1000;

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) {
    const m = Math.floor(diff / 60_000);
    return `${m}m ago`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / 3600_000);
    return `${h}h ago`;
  }
  const d = Math.floor(diff / DAY);
  return `${d}d ago`;
}

function expiresIn(deletedAt: number): string {
  const remaining = 30 * DAY - (Date.now() - deletedAt);
  if (remaining <= 0) return "expires soon";
  const days = Math.ceil(remaining / DAY);
  return `${days}d left`;
}

export function BinView({ onChanged }: Props) {
  const [cards, setCards] = useState<BinCard[]>([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const refresh = async () => {
    setLoading(true);
    const list = await api.listBin();
    setCards(list);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const restore = async (id: string) => {
    await api.restoreCard(id);
    setCards((prev) => prev.filter((c) => c.id !== id));
    onChanged();
  };

  const purge = async (id: string) => {
    const card = cards.find((c) => c.id === id);
    const ok = await confirm({
      title: "permanently delete?",
      body: card
        ? `"${card.title}" will be gone forever and won't be restorable from the bin.`
        : "this card will be gone forever and can't be restored.",
      confirmLabel: "delete forever",
      danger: true,
    });
    if (!ok) return;
    await api.purgeCard(id);
    setCards((prev) => prev.filter((c) => c.id !== id));
    onChanged();
  };

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center justify-between px-8 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <Trash size={18} className="text-blush-400" />
          <h2 className="text-2xl font-semibold tracking-tight">bin</h2>
          <span className="ml-1 text-sm text-ink-mute">
            {cards.length === 0 ? "empty" : `${cards.length} item${cards.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <p className="text-xs text-ink-mute">
          deleted cards stay here for 30 days
        </p>
      </header>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-ink-mute">
          loading...
        </div>
      ) : cards.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="max-w-md text-center">
            <p className="text-base text-ink-soft">nothing in the bin.</p>
            <p className="mt-2 text-sm text-ink-mute">
              deleted cards land here. they're recoverable for 30 days.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-8 pb-12 pt-2">
          <ul className="space-y-2">
            {cards.map((c) => {
              const fallback = c.accent ?? "lavender";
              const accent = accentClasses[fallback];
              return (
                <li
                  key={c.id}
                  className={clsx(
                    "group relative flex items-start gap-4 overflow-hidden rounded-card border border-border bg-surface px-4 py-3 shadow-card transition-shadow hover:shadow-cardHover",
                    "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:content-[''] before:opacity-70",
                    accent.dot.replace("bg-", "before:bg-")
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] leading-snug text-ink">
                      {c.title}
                    </p>
                    {c.body && (
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-ink-soft">
                        {c.body}
                      </p>
                    )}
                    <p className="mt-1.5 text-[11px] text-ink-mute">
                      from{" "}
                      <span className="text-ink-soft">{c.thread_name}</span>
                      {c.lane_name && (
                        <>
                          {" / "}
                          <span className="text-ink-soft">{c.lane_name}</span>
                        </>
                      )}
                      <span className="mx-2">·</span>
                      deleted {relativeTime(c.deleted_at)}
                      <span className="mx-2">·</span>
                      {expiresIn(c.deleted_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => restore(c.id)}
                      className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-xs text-ink-soft transition-colors hover:border-sage-200 hover:bg-sage-50 hover:text-ink"
                      title="restore to original location"
                    >
                      <RotateCcw size={11} /> restore
                    </button>
                    <button
                      onClick={() => purge(c.id)}
                      className="rounded-md p-1.5 text-ink-mute transition-colors hover:bg-blush-50 hover:text-blush-400"
                      title="permanently delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
